import { useEffect, useState } from 'react';

/**
 * Returns a value that trails `value` by at most `delayMs`. Used to keep
 * high-frequency input (keystrokes) from firing downstream work (network
 * queries, expensive re-renders) on every change.
 *
 * The timer resets whenever `value` changes, so rapid updates collapse
 * into a single settle. Zero or negative delays return `value` directly
 * so tests can disable the debounce deterministically.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (delayMs <= 0) {
      setDebounced(value);
      return undefined;
    }
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, delayMs]);

  return debounced;
}
